import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateTime } from 'luxon';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { LastMessage } from '@common/components/object-card';
import { AccessService, getKeysFromBinary } from '@common/services/access.service';
import { SortOrder } from '@common/utils/search-sort';

import { fakeDataMonitoringItem } from './monitoring-fake-data';
import {
  MonitoringItem,
  MonitoringItemTemplateParams,
  PaginatedResponse,
  RawMonitoringItem,
  SearchMonitoringGroupsListParams,
  SearchParams,
  SortParams,
} from './monitoring.types';

export enum AvailableIconE {
  IsIgnition = 'isIgnition',
  IsMotion = 'isMotion',
  ConnectedStatus = 'connectedStatus',
  LastActivity = 'lastActivity',
  SatellitesCount = 'satellitesCount',
  DriversAttached = 'driversAttached',
  TrailersAttached = 'trailersAttached',
  IsTrackingObject = 'isTrackingObject',
  IsShowMap = 'isShowMap',
}

export enum MonitoringContextMenuActionsE {
  OpenObjectProperties = 'openObjectProperties',
  BuildTrack = 'buildTrack',
  ShowMessage = 'showMessage',
  OpenRegisterEvents = 'openRegisterEvents',
  SendCommand = 'sendCommand',
}

export const contextMenuIcons: Record<MonitoringContextMenuActionsE, string> = {
  [MonitoringContextMenuActionsE.OpenObjectProperties]: 'common/assets/icons/settings.svg',
  [MonitoringContextMenuActionsE.BuildTrack]: 'common/assets/icons/tracks.svg',
  [MonitoringContextMenuActionsE.ShowMessage]: 'common/assets/icons/messages.svg',
  [MonitoringContextMenuActionsE.OpenRegisterEvents]: 'common/assets/icons/calendar-small.svg',
  [MonitoringContextMenuActionsE.SendCommand]: 'common/assets/icons/battery.svg',
};

export interface MonitoringContextMenuActionSettingsT {
  key: MonitoringContextMenuActionsE;
  control: FormControl<boolean>;
}

export const iconsList: Record<AvailableIconE, string> = {
  // [stateMotion_moving.svg, stateMotion_standing.svg]
  [AvailableIconE.IsMotion]: 'common/assets/icons/stateMotion_moving.svg', // Состояние движения/остановки
  [AvailableIconE.IsIgnition]: 'common/assets/icons/key.svg', // Состояние датчика зажигания
  [AvailableIconE.ConnectedStatus]: 'common/assets/icons/online.svg', // Подключение к серверу
  // [last_message_little.svg, last_message_middle.svg, last_message_many.svg, last_message_not_found.svg]
  [AvailableIconE.LastActivity]: 'common/assets/icons/last_message_many.svg', // Последняя активность
  [AvailableIconE.SatellitesCount]: 'common/assets/icons/satellites.svg', // Количество спутников
  // [assignedDrivers.svg, unAssignedDrivers.svg]
  [AvailableIconE.DriversAttached]: 'common/assets/icons/assignedDrivers.svg', // Назначенные водители
  [AvailableIconE.TrailersAttached]: 'common/assets/icons/trailers.svg', // Назначенные прицепы

  [AvailableIconE.IsTrackingObject]: 'common/assets/icons/track.svg', // Отслеживание
  [AvailableIconE.IsShowMap]: 'common/assets/icons/map.svg', // Скрыть/показать на карте
};

export type KeyNumIconObj = Record<number, AvailableIconE | null> | null;

export interface MonitoringItemIconsConfigTest {
  address: boolean;
  objectIcon: boolean;
  left: KeyNumIconObj;
  center: KeyNumIconObj;
}

export type TransformedMonitoringItemIconsConfigTest = Omit<
  MonitoringItemIconsConfigTest,
  'left' | 'center'
> & {
  left: Partial<Record<AvailableIconE, number>> | null;
  center: Partial<Record<AvailableIconE, number>> | null;
};
// ---------------------------------------------------------------------------

const IconKeys = [
  'isIgnition',
  'isMotion',
  'connectedStatus',
  'lastActivity',
  'satellitesCount',
  'driversAttached',
  'trailersAttached',
] as const;

type IconKey = (typeof IconKeys)[number];

type LeftType = Record<IconKey, number | undefined>;

type CenterType = Record<Exclude<IconKey, keyof LeftType>, number | undefined>;

// ---------------------------------------------------------------------------

export interface MonitoringItemIconsConfig {
  address: boolean;
  objectIcon: boolean;
  left: Record<string, number | null> | null;
  center: Record<string, number | null> | null;
}

@Injectable({
  providedIn: 'root',
})
export class MonitoringService {
  private readonly httpClient = inject(HttpClient);
  private readonly accessService = inject(AccessService);

  private _configListItem: MonitoringItemIconsConfig = {
    address: true,
    objectIcon: true,
    left: {
      isMotion: 1,
      isIgnition: 2,
    },
    center: {
      connectedStatus: 1,
      lastActivity: 2,
      satellitesCount: 3,
      driversAttached: 4,
      trailersAttached: 5,
    },
  };

  // Геттер для чтения значения
  get configListItem(): MonitoringItemIconsConfig {
    return this._configListItem;
  }

  // Сеттер для изменения значения
  set configListItem(value: MonitoringItemIconsConfig) {
    this._configListItem = value;
  }

  configContextMenu: Partial<Record<MonitoringContextMenuActionsE, number>> | null = {
    [MonitoringContextMenuActionsE.OpenObjectProperties]: 1,
    [MonitoringContextMenuActionsE.BuildTrack]: 2,
    [MonitoringContextMenuActionsE.ShowMessage]: 3,
    [MonitoringContextMenuActionsE.OpenRegisterEvents]: 4,
    [MonitoringContextMenuActionsE.SendCommand]: 5,
  };

  private _currentPage: 'list' | 'groups' = 'list';
  get currentPage(): 'list' | 'groups' {
    return this._currentPage;
  }

  set currentPage(value) {
    this._currentPage = value;
  }

  monitoringListSortParams: SortParams = {
    sortState: SortOrder.Asc,
    sortProp: new FormControl<string>('name', { nonNullable: true }),
    nullsPosition: new FormControl<'first' | 'last'>('last', {
      nonNullable: true,
    }),
  };

  monitoringGroupListSortParams: SortParams = {
    sortState: SortOrder.Asc,
    sortProp: new FormControl<string>('name', { nonNullable: true }),
    nullsPosition: new FormControl<'first' | 'last'>('last', {
      nonNullable: true,
    }),
  };

  monitoringListSearchParams: SearchParams = {
    searchControl: new FormControl<string>('', { nonNullable: true }),
    filterCriteria: { name: '', uniqueId: '' },
    filterModeControl: new FormControl('any', { nonNullable: true }),
    matchModeControl: new FormControl('includes', {
      nonNullable: true,
    }),
    caseSensitive: false,
    multipleSearch: true,
  };

  monitoringGroupListSearchParams: SearchMonitoringGroupsListParams = {
    searchValue: new FormControl('', { nonNullable: true }),
    searchByGroupName: new FormControl(true, { nonNullable: true }),
    searchByObjectName: new FormControl(false, { nonNullable: true }),
    searchByObjectId: new FormControl(false, { nonNullable: true }),
    multipleSearch: true,
  };
  expandedGroupIds: ReadonlySet<number> = new Set();

  transformedMonitoringFakeData: MonitoringItem = this.transformMonitoring(fakeDataMonitoringItem);

  /**
   * Получает список объектов мониторинга.
   * @returns {Observable<MonitoringItem[]>} - Наблюдаемый массив объектов мониторинга.
   */
  monitoringList(): Observable<MonitoringItem[]> {
    return this.httpClient
      .get<RawMonitoringItem[]>('objects/monitoring/')
      .pipe(map((rawItems) => rawItems.map((item) => this.transformMonitoring(item))));
  }

  monitoringListPaginated(totalItems: number, pageSize = 1000): Observable<MonitoringItem[]> {
    const totalPages = Math.ceil(totalItems / pageSize);
    const requests: Observable<PaginatedResponse<RawMonitoringItem>>[] = [];

    for (let page = 1; page <= totalPages; page++) {
      const params = new HttpParams()
        .set('page', page.toString())
        .set('per_page', pageSize.toString());

      const url = `objects/monitoring/`;
      requests.push(this.httpClient.get<PaginatedResponse<RawMonitoringItem>>(url, { params }));
    }

    return forkJoin(requests).pipe(
      map((responses: PaginatedResponse<RawMonitoringItem>[]) =>
        responses.flatMap((res) => res.results).map((item) => this.transformMonitoring(item)),
      ),
    );
  }

  pollMonitoringLastInfo(
    objectIds: number[],
    maxRt: number | null,
  ): Observable<{ maxRt: number; lastInfo: Record<string, any> }> {
    const url = 'drive-sense/api/v1/objects_info/actual';
    return this.httpClient.post<{ maxRt: number; lastInfo: Record<string, any> }>(url, {
      object_ids: objectIds,
      rt: maxRt,
    });
  }

  getMonitoringByIds(objectIds: number[], chunkSize = 1000): Observable<MonitoringItem[]> {
    const idChunks: number[][] = [];

    for (let i = 0; i < objectIds.length; i += chunkSize) {
      idChunks.push(objectIds.slice(i, i + chunkSize));
    }

    const requests = idChunks.map((chunk) => {
      const params = new HttpParams().set('objectIds', chunk.join(','));
      const url = 'objects/monitoring/';
      return this.httpClient.get<RawMonitoringItem[]>(url, { params });
    });

    return forkJoin(requests).pipe(
      map((responses: RawMonitoringItem[][]) =>
        responses.flat().map((item) => this.transformMonitoring(item)),
      ),
    );
  }

  // getMonitoringByIds(objectIds: number[]): Observable<MonitoringItem[]> {
  //   const params = new HttpParams().set('objectIds', objectIds.join(','));
  //   const url = 'objects/monitoring/';
  //
  //   return this.httpClient
  //     .get<RawMonitoringItem[]>(url, { params })
  //     .pipe(map((items) => items.map((item) => this.transformMonitoring(item))));
  // }

  /**
   * Преобразует исходный объект мониторинга в формат MonitoringItem.
   * @param {RawMonitoringItem} raw - Исходные данные мониторинга.
   * @returns {MonitoringItem} - Преобразованный объект мониторинга.
   */
  transformMonitoring(raw: RawMonitoringItem): MonitoringItem {
    return {
      ...raw,
      isIgnition: this.getParamsForIsIgnition(raw.isIgnition),
      isMotion: this.getParamsForIsMotion(raw.isMotion),
      driversAttached: this.getParamsForDriversAttached(raw.driversAttached),
      trailersAttached: this.getParamsForTrailersAttached(raw.trailersAttached),
      connectedStatus: this.getParamsForConnectedStatus(raw.connectedStatus),
      satellitesCount: this.getParamsForSatellitesCount(raw.lastMessage),
      lastActivity: this.getParamsForLastMessage(raw.lastMessage),
      currentUserAccess: this.getCurrentUserAccessAsArray(raw.currentUserAccess),
    };
  }

  getCurrentUserAccessAsArray(rawAccess: number | string[]): string[] {
    return typeof rawAccess === 'number'
      ? getKeysFromBinary(this.accessService.allAccessRights.objects, rawAccess)
      : rawAccess;
  }

  /**
   * Определяет параметры для отображения состояния движения объекта.
   * @param {boolean | null} isMotion - Состояние движения (`true`, `false` или `null`).
   * @returns {MonitoringItemTemplateParams} - Параметры отображения состояния движения.
   */
  getParamsForIsMotion(isMotion: boolean | null): MonitoringItemTemplateParams {
    return {
      value: isMotion,
      iconPath:
        isMotion === null
          ? 'common/assets/icons/stateMotion_no_data.svg'
          : isMotion
            ? 'common/assets/icons/stateMotion_moving.svg'
            : 'common/assets/icons/stateMotion_standing.svg',
      mainColor: isMotion === true ? 'green' : isMotion === false ? 'yellow' : undefined,
    };
  }

  /**
   * Определяет параметры для отображения состояния зажигания.
   * @param {boolean | null} isIgnition - Состояние зажигания (`true`, `false` или `null`).
   * @returns {MonitoringItemTemplateParams} - Параметры отображения состояния зажигания.
   */
  getParamsForIsIgnition(isIgnition: boolean | null): MonitoringItemTemplateParams {
    return {
      value: isIgnition,
      iconPath: 'common/assets/icons/key.svg',
      mainColor: isIgnition === true ? 'green' : isIgnition === false ? 'yellow' : undefined,
    };
  }

  /**
   * Определяет параметры для отображения состояния подключения.
   * @param {boolean | null} connectedStatus - Статус подключения (`true`, `false` или `null`).
   * @returns {MonitoringItemTemplateParams} - Параметры отображения состояния подключения.
   */
  getParamsForConnectedStatus(connectedStatus: boolean | null): MonitoringItemTemplateParams {
    return {
      value: connectedStatus,
      iconPath: connectedStatus
        ? 'common/assets/icons/online.svg'
        : 'common/assets/icons/offline.svg',
      mainColor: connectedStatus === true ? 'green' : undefined,
    };
  }

  /**
   * Определяет параметры для отображения статуса привязки водителей.
   * @param {boolean | null} driversAttached - Привязка водителей (`true`, `false` или `null`).
   * @returns {MonitoringItemTemplateParams} - Параметры отображения статуса водителей.
   */
  getParamsForDriversAttached(driversAttached: boolean | null): MonitoringItemTemplateParams {
    return {
      value: driversAttached,
      iconPath: driversAttached
        ? 'common/assets/icons/assignedDrivers.svg'
        : 'common/assets/icons/unAssignedDrivers.svg',
      mainColor: driversAttached === true ? 'green' : undefined,
    };
  }

  /**
   * Определяет параметры для отображения статуса привязки прицепов.
   * @param {boolean | null} trailersAttached - Привязка прицепов (`true`, `false` или `null`).
   * @returns {MonitoringItemTemplateParams} - Параметры отображения статуса прицепов.
   */
  getParamsForTrailersAttached(trailersAttached: boolean | null): MonitoringItemTemplateParams {
    return {
      value: trailersAttached,
      iconPath: 'common/assets/icons/trailers.svg',
      mainColor: trailersAttached === true ? 'green' : undefined,
    };
  }

  /**
   * Определяет параметры для отображения количества спутников.
   * @param {LastMessage | null} lastMessage - Последнее сообщение с данными о спутниках.
   * @returns {MonitoringItemTemplateParams} - Параметры отображения спутников.
   */
  getParamsForSatellitesCount(lastMessage: LastMessage | null): MonitoringItemTemplateParams {
    const sc = lastMessage?.pos?.sc;
    const tpos = lastMessage?.tpos;
    const { color } = this.getIconByDifferentDateTimes(tpos);

    return {
      value: sc,
      iconPath: 'common/assets/icons/satellites.svg',
      mainColor: sc == null ? undefined : sc > 3 ? 'green' : sc > 0 ? 'yellow' : 'red',
      ...(color && { before: { className: color } }),
    };
  }

  /**
   * Определяет цвет и длительность активности на основе времени последнего сообщения.
   * @param {any} datetime - Дата и время последнего сообщения.
   * @returns {{ color: 'green' | 'yellow' | 'red' | null; duration: number | null }} - Цвет и длительность.
   */
  getIconByDifferentDateTimes(datetime: any): {
    color: 'green' | 'yellow' | 'red' | null;
    duration: number | null;
  } {
    if (!datetime) return { color: null, duration: null };

    const timezone = sessionStorage.getItem('timezone') || 'utc';
    const currentDateTime = DateTime.utc().setZone(timezone);
    const lastMessageTime = DateTime.fromISO(datetime).setZone(timezone);
    const duration = currentDateTime.diff(lastMessageTime, 'minutes').minutes;

    return { color: duration < 10 ? 'green' : duration < 1440 ? 'yellow' : 'red', duration };
  }

  /**
   * Определяет параметры отображения последнего сообщения.
   * @param {LastMessage | null} lastMessage - Последнее сообщение объекта.
   * @returns {MonitoringItemTemplateParams} - Параметры отображения последней активности.
   */
  getParamsForLastMessage(lastMessage: LastMessage | null): MonitoringItemTemplateParams {
    const { color, duration } = this.getIconByDifferentDateTimes(lastMessage?.t);

    const iconPaths = {
      notFound: 'common/assets/icons/last_message_not_found.svg',
      little: 'common/assets/icons/last_message_little.svg',
      middle: 'common/assets/icons/last_message_middle.svg',
      many: 'common/assets/icons/last_message_many.svg',
    };

    return {
      value: lastMessage?.t,
      iconPath:
        duration == null
          ? iconPaths.notFound
          : duration < 10
            ? iconPaths.little
            : duration < 1440
              ? iconPaths.middle
              : iconPaths.many,
      ...(color && { before: { className: color } }),
    };
  }
}
